import { camelCase } from 'lodash';

import type { Typed } from 'services/api/fields/core';

export enum GroupTypeApiName {
  PROVIDER = 'provider',
  MANAGER = 'manager',
}

type Type = {
  apiName: GroupTypeApiName;
  displayName: string;
};

export type ManagerGroupTypeConfig = {
  canReceiveEscalations: boolean;
  patientPhysicianTeamRequired: boolean;
  patientTransferredFromRequired: boolean;
};

export type ProviderGroupTypeConfig = {
  designatesAcceptedPlans: boolean;
  hideDischargeSummaryPrompt: boolean;
  hideDischargedAma: boolean;
};

export interface GroupTypeOptions {
  id?: string;
  apiName?: string;
  displayName?: string;
  name?: string;
  isInpatient?: boolean;
  config?: ManagerGroupTypeConfig | ProviderGroupTypeConfig | Record<string, unknown>;
  createdAt?: string | null;
  updatedAt?: string | null;
  type?: Type;
  translations?: Record<string, string>;
  [key: string]: any;
}

function getDefaults(): GroupTypeOptions {
  return {
    id: '',
    apiName: '',
    displayName: '',
    name: '',
    isInpatient: false,
    config: {},
    createdAt: null,
    updatedAt: null,
    type: {
      apiName: GroupTypeApiName.PROVIDER,
      displayName: '',
    },
    translations: {},
  };
}

// Forward declare so _GroupType's constructor can return the infused type
let GroupType: new <T extends GroupTypeOptions = GroupTypeOptions>(options?: T) => Typed<_GroupType<T>, T>;

class _GroupType<T extends GroupTypeOptions = GroupTypeOptions> {
  apiName!: string;
  type!: { apiName: GroupTypeApiName; displayName: string };

  constructor(options?: T) {
    const opts = { ...getDefaults(), ...options };
    Object.assign(this, opts);

    this.type = opts.type ?? { apiName: GroupTypeApiName.PROVIDER, displayName: '' };
  }

  get isProvider() {
    return this.type.apiName === GroupTypeApiName.PROVIDER;
  }

  get isManager() {
    return this.type.apiName === GroupTypeApiName.MANAGER;
  }

  get camelCaseApiName() {
    return camelCase(this.apiName);
  }
}

GroupType = _GroupType as typeof GroupType; // eslint-disable-line prefer-const

export default GroupType;
export { GroupType };
export type GroupType<T extends GroupTypeOptions = GroupTypeOptions> = _GroupType<T> & Omit<T, keyof _GroupType<T>>;
